import React from 'react'

import { Stack, Typography, useTheme } from '@mui/material'

export interface FieldLabelProps {
  label?: React.ReactNode
  /** Appends a muted "(optional)" after the label. */
  optional?: boolean
  /**
   * Right-aligned content on the SAME row as the label — e.g. a "Clear"
   * action. Keeping it on the label row (rather than its own line above the
   * field) means it costs no vertical space and never shifts the field
   * beneath it (VERA-291).
   */
  action?: React.ReactNode
}

/**
 * Canonical field label for `apps/app` — the one place label typography, the
 * label→control gap, and the "(optional)" / right-aligned-action treatment
 * are defined, so every field (whether it renders through the shared
 * `Input` or a custom-controlled control like `MuiTelInput` or a date
 * picker) carries an identical label.
 *
 * Mirrors `@repo/ui`'s `FieldLabel` (packages/ui/components/input/
 * FieldLabel) in spirit, but is re-implemented here rather than imported:
 * `@repo/ui`'s gap (`mb: 2`) is tuned for its OWN theme, whose spacing unit
 * is 4px (`packages/ui/theme/spacing.ts`). `apps/app`'s theme
 * (`settings/mui/createMuiTheme.ts`) never overrides MUI's default 8px-per-
 * unit spacing, so the same `mb: 2` would render as 16px here — double the
 * intended gap. Hardcoding the gap in px sidesteps that mismatch instead of
 * silently shipping the wrong spacing.
 *
 * ## 14 / 500 / text.primary — and why it MOVED here
 *
 * This was 16px / 400 / text.secondary, which made the hierarchy read
 * BACKWARDS: `SectionLabel` (the uppercase caption naming a GROUP of fields —
 * DETAILS, HISTORY, GENERAL) is `body2` = 14px, so the label over ONE control
 * was LARGER than the heading over the whole section it sat inside. Size is
 * the first thing a reader resolves, so the structural element was losing to
 * the thing it contains.
 *
 * A field label is not body copy — it NAMES a control, so it takes the label
 * size (`body2`, 14) and label weight (500), at full contrast. Secondary grey
 * was the other half of the inversion: the label was simultaneously the
 * biggest and the faintest text in the group.
 *
 * **It must stay WEAKER than `SectionLabel`.** They now share a size and a
 * weight, and the separation is carried by `SectionLabel`'s UPPERCASE +
 * `letterSpacing 0.06em` + 32px of air above it — which is what makes it read
 * as a boundary rather than as another field name. So: do not raise this
 * label's weight or size, and do not add tracking to it. If this one ever
 * needs more emphasis, `SectionLabel` has to move first.
 */
export const FieldLabel: React.FC<FieldLabelProps> = ({ label, optional = false, action }) => {
  const theme = useTheme()

  if (!label && !action) return null

  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: '8px' }}>
      {label && (
        /* `body2` carries the 14px + its own 1.45 leading from the theme scale,
           so the only things declared here are the two that differ from body
           copy: the label weight and full contrast. Declaring a raw `fontSize`
           instead would leave the leading on the default `body1` (1.5) and add
           yet another line-height to the page. */
        <Typography variant='body2' sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
          {label}
          {optional && (
            /* `variant='inherit'` takes the size and leading from the label
               above rather than restating 14px. The qualifier steps back by
               WEIGHT and COLOUR only — no `opacity`. Opacity multiplied on top
               of an already-secondary token is how contrast drifts out of the
               palette's control: the rendered value stops being any colour the
               theme knows about, and a tenant whose `text.secondary` is already
               low-contrast gets pushed below legibility with nothing recording
               that it happened. */
            <Typography component='span' variant='inherit' sx={{ color: theme.palette.text.secondary, fontWeight: 400, ml: 0.75 }}>
              (optional)
            </Typography>
          )}
        </Typography>
      )}
      {action}
    </Stack>
  )
}
